import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBrand } from "@/contexts/brand-context";
import { useIntegrations } from "@/contexts/integrations-context";
import { analyticsIcons, cmsIcons, webmasterIcons } from "@/lib/icons";
import { pb } from "@/lib/pocketbase";
import { useState } from "react";
import { toast } from "sonner";
import { initiateGSCAuth } from "@/services/gsc";
import { ShopifyDialog } from "./integrations/Shopify";
import { SanityDialog } from "./integrations/Sanity";
import { ShopifySettingsButton } from "@/components/integrations/ShopifySettings";
import { AstroSettingsButton } from "@/components/integrations/AstroSettings";
import { WordPressSettingsButton } from "@/components/integrations/WordPressSettings";
import { SanitySettingsButton } from "@/components/integrations/SanitySettings";
import {
  ShopifyIntegration,
  AstroIntegration,
  WordPressIntegration,
  SanityIntegration,
} from "@/types/integration";

interface WordPressSettings {
  url: string;
  username: string;
  password: string;
}

interface AstroSettings {
  repositoryUrl: string;
  accessToken: string;
  contentPath: string;
}

export default function Integrations() {
  const { currentBrand } = useBrand();
  const { integrations, refreshIntegrations, getIntegrationsByType } =
    useIntegrations();
  const [wordpressDialogOpen, setWordpressDialogOpen] = useState(false);
  const [astroDialogOpen, setAstroDialogOpen] = useState(false);
  const [shopifyDialogOpen, setShopifyDialogOpen] = useState(false);
  const [sanityDialogOpen, setSanityDialogOpen] = useState(false);
  const [wordpressSettings, setWordpressSettings] = useState<WordPressSettings>(
    {
      url: "",
      username: "",
      password: "",
    },
  );
  const [astroSettings, setAstroSettings] = useState<AstroSettings>({
    repositoryUrl: "",
    accessToken: "",
    contentPath: "src/content",
  });
  const [loading, setLoading] = useState(false);
  const [gscProcessing, setGscProcessing] = useState(false);

  const getIntegrationStatus = (type: string) => {
    const typeIntegrations = getIntegrationsByType(type);
    return typeIntegrations.length > 0 &&
      typeIntegrations[0].status === "active"
      ? "active"
      : "inactive";
  };

  const handleWordpressConnect = async () => {
    if (!currentBrand) {
      toast.error("Please select a brand first");
      return;
    }

    setLoading(true);
    try {
      // Test WordPress connection
      const response = await fetch(
        `${pb.baseURL}/api/v1/wordpress/test-connection`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: pb.authStore.token,
          },
          body: JSON.stringify({
            brandId: currentBrand.id,
            ...wordpressSettings,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to connect to WordPress");
      }

      // Refresh integrations list
      await refreshIntegrations();

      setWordpressDialogOpen(false);
      toast.success("Successfully connected to WordPress");
    } catch (error) {
      console.error("Error connecting to WordPress:", error);
      toast.error("Failed to connect to WordPress");
    } finally {
      setLoading(false);
    }
  };

  const handleWordpressDisconnect = async () => {
    if (!currentBrand) return;

    setLoading(true);
    try {
      const integration = integrations.find((i) => i.type === "wordpress");
      if (integration) {
        await pb.collection("integrations").delete(integration.id);
        await refreshIntegrations();
      }

      toast.success("Successfully disconnected from WordPress");
    } catch (error) {
      console.error("Error disconnecting from WordPress:", error);
      toast.error("Failed to disconnect from WordPress");
    } finally {
      setLoading(false);
    }
  };

  const handleAstroConnect = async () => {
    if (!currentBrand) {
      toast.error("Please select a brand first");
      return;
    }

    setLoading(true);
    try {
      // Ensure we always use main branch
      const payloadWithMainBranch = {
        brandId: currentBrand.id,
        repositoryUrl: astroSettings.repositoryUrl,
        branch: "main", // Always use main branch
        accessToken: astroSettings.accessToken,
        contentPath: astroSettings.contentPath,
      };

      // Test Astro connection
      const response = await fetch(
        `${pb.baseURL}/api/v1/astro/test-connection`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: pb.authStore.token,
          },
          body: JSON.stringify(payloadWithMainBranch),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to connect to Astro");
      }

      // Refresh integrations list
      await refreshIntegrations();

      setAstroDialogOpen(false);
      toast.success("Successfully connected to Astro");
    } catch (error) {
      console.error("Error connecting to Astro:", error);
      toast.error("Failed to connect to Astro");
    } finally {
      setLoading(false);
    }
  };

  const handleAstroDisconnect = async () => {
    if (!currentBrand) return;

    setLoading(true);
    try {
      const integration = integrations.find((i) => i.type === "astro");
      if (integration) {
        await pb.collection("integrations").delete(integration.id);
        await refreshIntegrations();
      }

      toast.success("Successfully disconnected from Astro");
    } catch (error) {
      console.error("Error disconnecting from Astro:", error);
      toast.error("Failed to disconnect from Astro");
    } finally {
      setLoading(false);
    }
  };

  const handleShopifyDisconnect = async () => {
    if (!currentBrand) return;

    setLoading(true);
    try {
      const integration = integrations.find((i) => i.type === "shopify");
      if (integration) {
        await pb.collection("integrations").delete(integration.id);
        await refreshIntegrations();
      }

      toast.success("Successfully disconnected from Shopify");
    } catch (error) {
      console.error("Error disconnecting from Shopify:", error);
      toast.error("Failed to disconnect from Shopify");
    } finally {
      setLoading(false);
    }
  };

  // GSC OAuth flow: open popup, request webmasters.readonly, upsert integration
  const handleGSCConnect = async () => {
    if (!currentBrand) {
      return toast.error("Select a brand first");
    }
    setGscProcessing(true);
    let popup: Window | null = null;
    let pollTimer: number | null = null;
    try {
      popup = window.open("", "oauthGSC", "width=500,height=600");
      console.log("[GSC] popup instance:", popup);
      if (!popup) {
        setGscProcessing(false);
        alert("Popup was blocked. Please allow popups for this site.");
        return;
      }
      // Poll popup URL to detect redirects and closure
      pollTimer = window.setInterval(() => {
        if (!popup) return;
        if (popup.closed) {
          console.log("[GSC] popup closed");
          if (pollTimer) clearInterval(pollTimer);
        }
      }, 1000);
      await initiateGSCAuth(currentBrand.id, "webmaster");
      if (pollTimer) clearInterval(pollTimer);
      // After successful backend OAuth flow, refresh integrations and show success toast
      toast.success("Search Console connected");
      await refreshIntegrations();
    } catch (err: unknown) {
      if (err && typeof err === "object" && "message" in err) {
        toast.error(
          (err as { message?: string }).message || "Could not connect to GSC",
        );
      } else {
        toast.error("Could not connect to GSC");
      }
    } finally {
      setGscProcessing(false);
      if (popup && !popup.closed) popup.close();
      if (pollTimer) clearInterval(pollTimer);
    }
  };

  const handleGSCDisconnect = async () => {
    const existing = integrations.find((i) => i.type === "search console");
    if (!existing) return;
    setGscProcessing(true);
    try {
      await pb.collection("integrations").delete(existing.id);
      toast.success("Google Search Console disconnected");
      await refreshIntegrations();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not disconnect GSC";
      toast.error(message);
    } finally {
      setGscProcessing(false);
    }
  };

  const handleSanityDisconnect = async () => {
    if (!currentBrand) return;

    setLoading(true);
    try {
      const integration = integrations.find((i) => i.type === "sanity");
      if (integration) {
        await pb.collection("integrations").delete(integration.id);
        await refreshIntegrations();
      }

      toast.success("Successfully disconnected from Sanity");
    } catch (error) {
      console.error("Error disconnecting from Sanity:", error);
      toast.error("Failed to disconnect from Sanity");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Integrations</h2>
          <p className="text-muted-foreground">
            Connect with your favorite tools and platforms.
          </p>
        </div>
      </div>

      {/* CMS Integrations */}
      <Card>
        <CardHeader>
          <CardTitle>CMS Integrations</CardTitle>
          <CardDescription>
            Connect with your content management systems
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(cmsIcons).map(([name, icon]) => {
              const status = getIntegrationStatus(name.toLowerCase());
              return (
                <Card key={name} className="relative">
                  <CardHeader className="flex flex-row items-center space-x-4">
                    <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center">
                      {icon}
                    </div>
                    <div className="flex-1">
                      <CardTitle>{name}</CardTitle>
                      <CardDescription>
                        Connect with your {name} account
                      </CardDescription>
                    </div>
                    {(name === "WordPress" ||
                      name === "Astro" ||
                      name === "Shopify" ||
                      name === "Sanity") &&
                      status === "active" && (
                        <Badge
                          variant="outline"
                          className="absolute top-4 right-4 bg-green-50 text-green-700 border-green-200"
                        >
                          Connected
                        </Badge>
                      )}
                  </CardHeader>
                  <CardContent className="flex items-center gap-2">
                    {name === "WordPress" ||
                    name === "Astro" ||
                    name === "Shopify" ||
                    name === "Sanity" ? (
                      status !== "active" && (
                        <Badge
                          variant="outline"
                          className="bg-slate-50 text-slate-700 border-slate-200"
                        >
                          Not Connected
                        </Badge>
                      )
                    ) : (
                      <Badge
                        variant="outline"
                        className="bg-yellow-50 text-yellow-700 border-yellow-200"
                      >
                        Coming Soon
                      </Badge>
                    )}
                    {name === "WordPress" ? (
                      <div className="flex items-center gap-2 ml-auto">
                        {status === "active" && (
                          <WordPressSettingsButton
                            brandId={currentBrand?.id || ""}
                            integration={
                              getIntegrationsByType(
                                "wordpress",
                              )[0] as WordPressIntegration
                            }
                            onSuccess={() => refreshIntegrations()}
                          />
                        )}
                        <Button
                          variant={status === "active" ? "outline" : "default"}
                          onClick={() =>
                            status === "active"
                              ? handleWordpressDisconnect()
                              : setWordpressDialogOpen(true)
                          }
                        >
                          {status === "active" ? "Disconnect" : "Connect"}
                        </Button>
                      </div>
                    ) : name === "Astro" ? (
                      <div className="flex items-center gap-2 ml-auto">
                        {status === "active" && (
                          <AstroSettingsButton
                            brandId={currentBrand?.id || ""}
                            integration={
                              getIntegrationsByType(
                                "astro",
                              )[0] as AstroIntegration
                            }
                            onSuccess={() => refreshIntegrations()}
                          />
                        )}
                        <Button
                          variant={status === "active" ? "outline" : "default"}
                          onClick={() =>
                            status === "active"
                              ? handleAstroDisconnect()
                              : setAstroDialogOpen(true)
                          }
                        >
                          {status === "active" ? "Disconnect" : "Connect"}
                        </Button>
                      </div>
                    ) : name === "Shopify" ? (
                      <div className="flex items-center gap-2 ml-auto">
                        {status === "active" && (
                          <ShopifySettingsButton
                            brandId={currentBrand?.id || ""}
                            integration={
                              getIntegrationsByType(
                                "shopify",
                              )[0] as ShopifyIntegration
                            }
                            onSuccess={() => refreshIntegrations()}
                          />
                        )}
                        <Button
                          variant={status === "active" ? "outline" : "default"}
                          onClick={() =>
                            status === "active"
                              ? handleShopifyDisconnect()
                              : setShopifyDialogOpen(true)
                          }
                        >
                          {status === "active" ? "Disconnect" : "Connect"}
                        </Button>
                      </div>
                    ) : name === "Sanity" ? (
                      <div className="flex items-center gap-2 ml-auto">
                        {status === "active" && (
                          <SanitySettingsButton
                            brandId={currentBrand?.id || ""}
                            integration={
                              getIntegrationsByType(
                                "sanity",
                              )[0] as SanityIntegration
                            }
                            onSuccess={() => refreshIntegrations()}
                          />
                        )}
                        <Button
                          variant={status === "active" ? "outline" : "default"}
                          onClick={() =>
                            status === "active"
                              ? handleSanityDisconnect()
                              : setSanityDialogOpen(true)
                          }
                        >
                          {status === "active" ? "Disconnect" : "Connect"}
                        </Button>
                      </div>
                    ) : (
                      <Button className="ml-auto" disabled>
                        Connect
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Analytics Integrations */}
      <Card>
        <CardHeader>
          <CardTitle>Analytics Integrations</CardTitle>
          <CardDescription>
            Connect with analytics and tracking tools
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(analyticsIcons).map(([name, icon]) => {
              return (
                <Card key={name}>
                  <CardHeader className="flex flex-row items-center space-x-4">
                    <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center">
                      {icon}
                    </div>
                    <div>
                      <CardTitle>{name}</CardTitle>
                      <CardDescription>
                        Connect with your {name} account
                      </CardDescription>
                    </div>
                  </CardHeader>
                  <CardContent className="flex items-center justify-between">
                    <Badge
                      variant="outline"
                      className="bg-yellow-50 text-yellow-700 border-yellow-200"
                    >
                      Coming Soon
                    </Badge>
                    <Button className="ml-2" disabled>
                      Connect
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Webmaster Integrations */}
      <Card>
        <CardHeader>
          <CardTitle>Webmaster Integrations</CardTitle>
          <CardDescription>
            Connect with webmaster and SEO tools
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(webmasterIcons).map(([name, icon]) => {
              if (name !== "Google Search Console") {
                return (
                  <Card key={name}>
                    <CardHeader className="flex flex-row items-center space-x-4">
                      <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center">
                        {icon}
                      </div>
                      <div>
                        <CardTitle>{name}</CardTitle>
                        <CardDescription>
                          Connect with your {name} account
                        </CardDescription>
                      </div>
                    </CardHeader>
                    <CardContent className="flex items-center justify-between">
                      <Badge
                        variant="outline"
                        className="bg-yellow-50 text-yellow-700 border-yellow-200"
                      >
                        Coming Soon
                      </Badge>
                      <Button className="ml-2" disabled>
                        Connect
                      </Button>
                    </CardContent>
                  </Card>
                );
              }
              const integ = integrations.find(
                (i) => i.type === "search console",
              );
              const status = integ?.status === "active";
              return (
                <Card key={name}>
                  <CardHeader className="flex flex-row items-center space-x-4">
                    <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center">
                      {icon}
                    </div>
                    <div>
                      <CardTitle>{name}</CardTitle>
                      <CardDescription>
                        Connect with your {name} account
                      </CardDescription>
                    </div>
                  </CardHeader>
                  <CardContent className="flex items-center justify-between">
                    <Badge
                      variant={status ? "outline" : "outline"}
                      className={
                        status
                          ? "bg-green-50 text-green-700 border-green-200"
                          : "bg-yellow-50 text-yellow-700 border-yellow-200"
                      }
                    >
                      {status ? "Connected" : "Not Connected"}
                    </Badge>
                    <Button
                      className="ml-2"
                      variant={status ? "destructive" : "default"}
                      onClick={status ? handleGSCDisconnect : handleGSCConnect}
                      disabled={gscProcessing}
                    >
                      {gscProcessing
                        ? status
                          ? "Disconnecting…"
                          : "Connecting…"
                        : status
                          ? "Disconnect"
                          : "Connect"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* WordPress Connection Dialog */}
      <Dialog open={wordpressDialogOpen} onOpenChange={setWordpressDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Connect WordPress</DialogTitle>
            <DialogDescription>
              Enter your WordPress credentials to connect your website
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6">
            {/* Instructions */}
            <div className="space-y-4">
              <div className="rounded-lg border p-4 bg-slate-50">
                <h4 className="font-medium mb-2">
                  How to generate Application Password:
                </h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Log in to your WordPress admin dashboard</li>
                  <li>Go to Users → Your Profile</li>
                  <li>Scroll down to "Application Passwords" section</li>
                  <li>
                    Enter a name for this application (e.g., "Content Creator")
                  </li>
                  <li>Click "Add New Application Password"</li>
                  <li>
                    Copy the generated password (you won't be able to see it
                    again)
                  </li>
                </ol>
                <p className="text-sm text-muted-foreground mt-4">
                  Note: Make sure your WordPress site has the REST API enabled
                  and is running WordPress 5.6 or later.
                </p>
              </div>
            </div>

            {/* Connection Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="url">WordPress URL</Label>
                <Input
                  id="url"
                  placeholder="https://your-wordpress-site.com"
                  value={wordpressSettings.url}
                  onChange={(e) =>
                    setWordpressSettings((prev) => ({
                      ...prev,
                      url: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="username">WordPress Username</Label>
                <Input
                  id="username"
                  placeholder="WordPress username"
                  value={wordpressSettings.username}
                  onChange={(e) =>
                    setWordpressSettings((prev) => ({
                      ...prev,
                      username: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Application Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="WordPress application password"
                  value={wordpressSettings.password}
                  onChange={(e) =>
                    setWordpressSettings((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                />
                <p className="text-sm text-muted-foreground">
                  This is not your regular WordPress password. See instructions
                  on the left.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWordpressDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleWordpressConnect}
              disabled={
                loading ||
                !wordpressSettings.url ||
                !wordpressSettings.username ||
                !wordpressSettings.password
              }
            >
              {loading ? "Connecting..." : "Connect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Astro Connection Dialog */}
      <Dialog open={astroDialogOpen} onOpenChange={setAstroDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Connect Astro</DialogTitle>
            <DialogDescription>
              Enter your Git repository details to connect your Astro website
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6">
            {/* Instructions */}
            <div className="space-y-4">
              <div className="rounded-lg border p-4 bg-slate-50">
                <h4 className="font-medium mb-2">
                  How to set up Git integration for your Astro site:
                </h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  <li>
                    Make sure your Astro site is in a Git repository (GitHub)
                  </li>
                  <li>
                    Generate a GitHub personal access token with{" "}
                    <strong>read/write permissions</strong> for both{" "}
                    <strong>Contents</strong> and <strong>Pull Requests</strong>
                  </li>
                  <li>
                    Enter your repository URL (we'll use the default{" "}
                    <code>main</code> branch)
                  </li>
                  <li>
                    Identify the path to your content directory (typically{" "}
                    <code>src/content</code>)
                  </li>
                </ol>
                <p className="text-sm text-muted-foreground mt-4">
                  Note: This integration will commit all content changes (text
                  and images) in a single commit and create a pull request.
                </p>
              </div>
            </div>

            {/* Connection Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="repository-url">Repository URL</Label>
                <Input
                  id="repository-url"
                  placeholder="https://github.com/username/your-astro-site"
                  value={astroSettings.repositoryUrl}
                  onChange={(e) =>
                    setAstroSettings((prev) => ({
                      ...prev,
                      repositoryUrl: e.target.value,
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  The URL of your GitHub repository, e.g.,{" "}
                  <code>https://github.com/username/repo</code>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content-path">Content Path</Label>
                <Input
                  id="content-path"
                  placeholder="src/content"
                  value={astroSettings.contentPath}
                  onChange={(e) =>
                    setAstroSettings((prev) => ({
                      ...prev,
                      contentPath: e.target.value,
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  The directory where content is stored in your Astro site
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="access-token">GitHub Access Token</Label>
                <Input
                  id="access-token"
                  type="password"
                  placeholder="Your GitHub personal access token"
                  value={astroSettings.accessToken}
                  onChange={(e) =>
                    setAstroSettings((prev) => ({
                      ...prev,
                      accessToken: e.target.value,
                    }))
                  }
                />
                <p className="text-sm text-muted-foreground">
                  This is a secure token used to authenticate Git operations.
                </p>
                <div className="text-xs text-muted-foreground mt-1">
                  <strong>
                    How to generate a{" "}
                    <span className="text-green-700">fine-grained</span> GitHub
                    personal access token:
                  </strong>
                  <ol className="list-decimal list-inside mt-1 mb-1">
                    <li>
                      Go to{" "}
                      <a
                        href="https://github.com/settings/tokens?type=beta"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-blue-600"
                      >
                        GitHub's fine-grained token creation page
                      </a>
                      .
                    </li>
                    <li>
                      Give your token a name and set an expiration (recommended:
                      90 days).
                    </li>
                    <li>
                      Under <strong>Repository access</strong>, select{" "}
                      <strong>Only select repositories</strong> and choose your
                      Astro website repository.
                    </li>
                    <li>
                      Under <strong>Repository permissions</strong>, set{" "}
                      <strong>Contents</strong> to <code>Read and write</code>.
                    </li>
                    <li>
                      Under <strong>Repository permissions</strong>, also set{" "}
                      <strong>Pull Requests</strong> to{" "}
                      <code>Read and write</code>.
                    </li>
                    <li>
                      Click <strong>Generate token</strong> and copy it (you
                      won't be able to see it again).
                    </li>
                  </ol>
                  <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800">
                    <strong>Important:</strong> Both <strong>Contents</strong>{" "}
                    and <strong>Pull Requests</strong> permissions are required
                    for this integration to work properly.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAstroDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAstroConnect}
              disabled={
                loading ||
                !astroSettings.repositoryUrl ||
                !astroSettings.accessToken
              }
            >
              {loading ? "Connecting..." : "Connect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shopify Connection Dialog */}
      <ShopifyDialog
        open={shopifyDialogOpen}
        onOpenChange={setShopifyDialogOpen}
        onSuccess={() => {
          // Refresh integrations list
          refreshIntegrations().catch((error) => {
            console.error("Error fetching integrations:", error);
          });
        }}
        brandId={currentBrand?.id || ""}
      />

      {/* Sanity Connection Dialog */}
      <SanityDialog
        open={sanityDialogOpen}
        onOpenChange={setSanityDialogOpen}
        onSuccess={() => {
          // Refresh integrations list
          refreshIntegrations().catch((error) => {
            console.error("Error fetching integrations:", error);
          });
        }}
        brandId={currentBrand?.id || ""}
      />
    </div>
  );
}
